import type { BacklogConnectionStatus } from '@ai-meeting/shared'

/**
 * What the Copilot panel has to ask the reader for before a Backlog question
 * can be answered (BRAI-689).
 *
 * - `none` — nothing has been read yet, or nothing could be. Never returned by
 *   {@link copilotSetupNeed}; it is what the reading hook holds until an
 *   answer arrives, so the strip does not flash on its way in.
 * - `connect` / `reauth` — there is no usable grant behind this reader.
 * - `pick-project` — the grant works and there are projects to choose from.
 * - `no-projects` — the grant works and belongs to no project synced here,
 *   so there is nothing to pick and saying so is all this screen can do.
 */
export type CopilotSetupNeed = 'none' | 'connect' | 'reauth' | 'pick-project' | 'no-projects'

interface CopilotSetupInput {
  /** The answer `/api/projects` gave, which carries the grant's state as data. */
  status: BacklogConnectionStatus
  /** How many projects that grant can see. */
  projectCount: number
}

/**
 * Decide what the strip should offer, before the reader asks a question that
 * cannot be answered.
 *
 * The panel used to find this out the slow way: the reader typed a Backlog
 * question, waited, and got back a refusal naming a prerequisite — one round
 * trip per thing they were missing. Both prerequisites are knowable the moment
 * the panel opens, so they are read then and shown as something to act on.
 *
 * The connection is checked first because a reader with no grant cannot be
 * offered a list of projects: `/api/projects` answers every status but
 * `ACTIVE` with an empty list, so a missing grant and a grant that belongs to
 * nothing are indistinguishable from the list alone.
 *
 * What the meeting's project is set to is deliberately not an input. The
 * picker is a control rather than a prompt, and a control that disappears the
 * moment it is used is one the reader cannot use twice — which is exactly
 * what the first cut of this did. Whether the meeting is *missing* a project
 * is a separate question, and the strip answers it with a line above the
 * picker rather than by taking the picker away.
 *
 * @returns Which control to put in front of the reader.
 */
export function copilotSetupNeed({ status, projectCount }: CopilotSetupInput): CopilotSetupNeed {
  if (status === 'CONNECTION_REQUIRED') return 'connect'
  if (status === 'REAUTH_REQUIRED') return 'reauth'
  return projectCount > 0 ? 'pick-project' : 'no-projects'
}
